/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useRef } from 'preact/hooks';
import { verticalScrollbarStyles } from './themes/TimeComponentStyles.css';
import { TimeComponentScrollbar } from './TimeComponentScrollbar';

type Props = {
  thumbHeight: number;
  thumbTop: number;
  draggableRegion: { xMin: number; xMax: number; yMin: number; yMax: number };
  getNewRange: (newThumbTop: number) => { newMin: number; newMax: number };
  onRangeChange: (newMin: number, newMax: number) => void;
  width: number;
  height: number;
};

export function TimeComponentVerticalScrollbar({ getNewRange, onRangeChange, ...props }: Props) {
  const prevRange = useRef({ min: 0, max: 0 });
  const processRange = (newMin: number, newMax: number) => {
    const { min: prevMin, max: prevMax } = prevRange.current;
    const isRangeChanged = newMin !== prevMin || newMax !== prevMax;
    isRangeChanged && onRangeChange(newMin, newMax);
    prevRange.current = { min: newMin, max: newMax };
  };

  const processDragRangeYChange = (y: number, thumbOffsetY: number) => {
    const { newMin, newMax } = getNewRange(Math.max(0, y - thumbOffsetY));
    processRange(newMin, newMax);
  };

  const processClickRangeYChange = (y: number) => {
    const { yMin, yMax } = props.draggableRegion;
    const halfThumbWidth = (yMax - yMin) / 2;
    const { newMin, newMax } = getNewRange(Math.max(0, y - halfThumbWidth));
    processRange(newMin, newMax);
  };

  return (
    <TimeComponentScrollbar
      {...props}
      processDragRangeYChange={processDragRangeYChange}
      processClickRangeYChange={processClickRangeYChange}
      styles={verticalScrollbarStyles}
    />
  );
}
